import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '../ui/dialog';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Shield, Smartphone, Monitor, Tablet, Globe, ShieldCheck, ShieldOff, Ban, Loader2, CheckCircle, X, Lock, Bell, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

function SecurityDialog() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tfaEnabled, setTfaEnabled] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [isGoogleOnly, setIsGoogleOnly] = useState(false);
  const [suspiciousEvents, setSuspiciousEvents] = useState([]);
  const [suspiciousLoading, setSuspiciousLoading] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const data = await api.get('/security/settings');
      setTfaEnabled(data.two_factor_enabled);
      setNotificationsEnabled(data.login_notifications_enabled);
      setIsGoogleOnly(data.is_google_only);
    } catch (e) { /* */ }
  }, []);

  const loadDevices = useCallback(async () => {
    setDevicesLoading(true);
    try {
      const data = await api.get('/security/devices');
      setDevices(data.devices || []);
    } catch (e) { /* */ }
    setDevicesLoading(false);
  }, []);

  const loadSuspicious = useCallback(async () => {
    setSuspiciousLoading(true);
    try {
      const data = await api.get('/security/suspicious');
      setSuspiciousEvents((data.events || []).filter(e => !e.dismissed));
    } catch (e) { /* */ }
    setSuspiciousLoading(false);
  }, []);

  useEffect(() => {
    if (open) { loadSettings(); loadDevices(); loadSuspicious(); }
  }, [open, loadSettings, loadDevices, loadSuspicious]);

  const toggle2FA = async () => {
    if (tfaEnabled) {
      if (isGoogleOnly) {
        // Google-only users — disable directly (no password)
        setLoading(true);
        try {
          await api.post('/security/2fa/disable', {});
          setTfaEnabled(false);
          toast.success('2FA disabled');
        } catch (e) {
          toast.error(e.message || 'Failed to disable 2FA');
        }
        setLoading(false);
      } else {
        // Password users — show password confirmation
        setShowDisableConfirm(true);
        setDisablePassword('');
      }
      return;
    }
    setLoading(true);
    try {
      await api.post('/security/2fa/enable');
      setTfaEnabled(true);
      toast.success('2FA enabled! You will need a code at next login.');
    } catch (e) {
      toast.error(e.message || 'Failed to enable 2FA');
    }
    setLoading(false);
  };

  const confirmDisable2FA = async () => {
    if (!disablePassword.trim()) {
      toast.error('Please enter your password');
      return;
    }
    setLoading(true);
    try {
      await api.post('/security/2fa/disable', { password: disablePassword });
      setTfaEnabled(false);
      setShowDisableConfirm(false);
      setDisablePassword('');
      toast.success('2FA disabled');
    } catch (e) {
      toast.error(e.message || 'Incorrect password');
    }
    setLoading(false);
  };

  const trustDevice = async () => {
    try {
      await api.post('/security/devices/trust');
      toast.success('This device is now trusted for 30 days');
      loadDevices();
    } catch (e) { toast.error('Failed to trust device'); }
  };

  const toggleNotifications = async () => {
    setNotifLoading(true);
    try {
      const data = await api.post('/security/notifications/toggle');
      setNotificationsEnabled(data.login_notifications_enabled);
      toast.success(data.login_notifications_enabled ? 'Login notifications enabled' : 'Login notifications disabled');
    } catch (e) {
      toast.error('Failed to update notifications');
    }
    setNotifLoading(false);
  };

  const dismissSuspicious = async (eventId) => {
    try {
      await api.post(`/security/suspicious/${eventId}/dismiss`);
      setSuspiciousEvents(prev => prev.filter(e => e.event_id !== eventId));
    } catch (e) { toast.error('Failed to dismiss'); }
  };

  const toggleBlock = async (deviceId, block) => {
    try {
      await api.post('/security/devices/block', { device_id: deviceId, blocked: block });
      toast.success(block ? 'Device blocked' : 'Device unblocked');
      loadDevices();
    } catch (e) { toast.error('Failed'); }
  };

  const untrustDevice = async (deviceId) => {
    try {
      await api.post('/security/devices/untrust', { device_id: deviceId, blocked: false });
      toast.success('Device untrusted');
      loadDevices();
    } catch (e) { toast.error('Failed'); }
  };

  const getDeviceIcon = (type) => {
    if (type === 'Mobile') return <Smartphone className="h-4 w-4" />;
    if (type === 'Tablet') return <Tablet className="h-4 w-4" />;
    return <Monitor className="h-4 w-4" />;
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch (e) { return '-'; }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          className="flex flex-col items-center gap-2 p-4 bg-slate-800/50 rounded-2xl hover:bg-slate-800 transition-colors border border-slate-700/50"
          data-testid="security-btn"
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
            <Shield className="h-5 w-5 text-white" />
          </div>
          <span className="text-sm text-white">Security</span>
        </button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md max-h-[85vh] overflow-y-auto p-0">
        <DialogHeader className="p-4 pb-0">
          <DialogTitle className="text-white flex items-center gap-2">
            <Shield className="h-5 w-5 text-cyan-400" />
            Security Settings
          </DialogTitle>
        </DialogHeader>

        <div className="p-4 space-y-5">
          {/* 2FA Toggle */}
          <div className="bg-slate-800/50 rounded-xl p-4 space-y-3 border border-slate-700/50" data-testid="2fa-section">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ShieldCheck className="h-5 w-5 text-cyan-400" />
                <div>
                  <p className="text-sm font-medium text-white">Two-Factor Authentication</p>
                  <p className="text-[11px] text-slate-500">6-digit code via email on each login</p>
                </div>
              </div>
              <Switch
                checked={tfaEnabled}
                onCheckedChange={toggle2FA}
                disabled={loading}
                data-testid="2fa-toggle"
              />
            </div>
            {tfaEnabled && (
              <p className="text-[11px] text-cyan-400/70 bg-cyan-500/10 rounded-lg px-3 py-2">
                Active. A verification code will be sent to your email every time you sign in from an untrusted device.
              </p>
            )}
            {showDisableConfirm && (
              <div className="bg-slate-700/50 rounded-lg p-3 space-y-2 border border-slate-600/50" data-testid="disable-2fa-confirm">
                <p className="text-xs text-slate-300 flex items-center gap-1.5">
                  <Lock className="h-3 w-3 text-amber-400" />
                  Enter your password to disable 2FA
                </p>
                <input
                  type="password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && confirmDisable2FA()}
                  placeholder="Your password"
                  className="w-full bg-slate-800 text-white text-sm rounded-lg px-3 py-2 border border-slate-600 focus:outline-none focus:border-cyan-500"
                  autoFocus
                  data-testid="disable-2fa-password"
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={confirmDisable2FA} disabled={loading}
                    className="bg-red-600 hover:bg-red-500 h-7 text-xs flex-1" data-testid="disable-2fa-confirm-btn">
                    {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Disable 2FA'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setShowDisableConfirm(false); setDisablePassword(''); }}
                    className="text-slate-400 h-7 text-xs" data-testid="disable-2fa-cancel-btn">
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Trust Current Device */}
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50" data-testid="trust-section">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Smartphone className="h-5 w-5 text-green-400" />
                <div>
                  <p className="text-sm font-medium text-white">Trust This Device</p>
                  <p className="text-[11px] text-slate-500">Skip 2FA for 30 days on this device</p>
                </div>
              </div>
              <Button size="sm" onClick={trustDevice} className="bg-green-600 hover:bg-green-500 h-7 text-xs" data-testid="trust-this-btn">
                Trust
              </Button>
            </div>
          </div>

          {/* Login Notifications */}
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50" data-testid="login-notifications-section">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell className="h-5 w-5 text-amber-400" />
                <div>
                  <p className="text-sm font-medium text-white">Login Notifications</p>
                  <p className="text-[11px] text-slate-500">Email alert when a new device signs in</p>
                </div>
              </div>
              <Switch
                checked={notificationsEnabled}
                onCheckedChange={toggleNotifications}
                disabled={notifLoading}
                data-testid="login-notifications-toggle"
              />
            </div>
            {notificationsEnabled && (
              <p className="text-[11px] text-amber-400/70 bg-amber-500/10 rounded-lg px-3 py-2 mt-3">
                You'll receive an email whenever a new device logs into your account.
              </p>
            )}
          </div>

          {/* Suspicious Activity */}
          {suspiciousEvents.length > 0 && (
            <div className="space-y-3" data-testid="suspicious-section">
              <h3 className="text-sm font-semibold text-red-400 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Suspicious Activity ({suspiciousEvents.length})
              </h3>
              <div className="space-y-2">
                {suspiciousEvents.slice(0, 5).map((event) => (
                  <div key={event.event_id} className="bg-red-500/10 border border-red-500/30 rounded-xl p-3" data-testid={`suspicious-${event.event_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap gap-1 mb-1">
                          {event.reason_labels.map((label, i) => (
                            <span key={i} className="text-[10px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded-full">{label}</span>
                          ))}
                        </div>
                        <p className="text-xs text-white">{event.browser} on {event.os}</p>
                        <p className="text-[11px] text-slate-500">
                          {event.city && event.country ? `${event.city}, ${event.country}` : event.country || 'Unknown location'}
                          {' '}&middot; IP: {event.ip}
                          {event.isp && <> &middot; {event.isp}</>}
                        </p>
                        <p className="text-[11px] text-slate-600">{formatDate(event.timestamp)}</p>
                      </div>
                      <button onClick={() => dismissSuspicious(event.event_id)}
                        className="text-slate-500 hover:text-slate-300 p-1 flex-shrink-0" title="Dismiss"
                        data-testid={`dismiss-${event.event_id}`}>
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Devices / Login History */}
          <div className="space-y-3" data-testid="devices-section">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
                <Globe className="h-4 w-4 text-slate-400" />
                Login History & Devices
              </h3>
              <button onClick={loadDevices} className="text-xs text-cyan-400 hover:text-cyan-300">Refresh</button>
            </div>

            {devicesLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
              </div>
            ) : devices.length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-4">No login history yet</p>
            ) : (
              <div className="space-y-2">
                {devices.map((device) => (
                  <div
                    key={device.device_id}
                    className={`rounded-xl p-3 border transition-colors ${
                      device.is_blocked
                        ? 'bg-red-500/10 border-red-500/30'
                        : device.is_current
                        ? 'bg-cyan-500/10 border-cyan-500/30'
                        : 'bg-slate-800/50 border-slate-700/50'
                    }`}
                    data-testid={`device-${device.device_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 ${device.is_blocked ? 'text-red-400' : device.is_current ? 'text-cyan-400' : 'text-slate-400'}`}>
                        {getDeviceIcon(device.device_type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-white truncate">{device.browser}</span>
                          {device.is_current && (
                            <span className="text-[10px] bg-cyan-500/20 text-cyan-400 px-1.5 py-0.5 rounded-full">Current</span>
                          )}
                          {device.is_trusted && (
                            <span className="text-[10px] bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded-full">Trusted</span>
                          )}
                          {device.is_blocked && (
                            <span className="text-[10px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded-full">Blocked</span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-500">{device.os} &middot; {device.device_type}{device.country ? ` &middot; ${device.country}` : ''}</p>
                        <p className="text-[11px] text-slate-600">IP: {device.ip}{device.city ? ` (${device.city})` : ''} &middot; Last: {formatDate(device.last_login)} &middot; Logins: {device.login_count}</p>
                      </div>
                    </div>

                    {/* Device actions */}
                    {!device.is_current && (
                      <div className="flex gap-2 mt-2 pt-2 border-t border-slate-700/50">
                        {device.is_blocked ? (
                          <Button size="sm" variant="ghost" onClick={() => toggleBlock(device.device_id, false)}
                            className="text-green-400 hover:bg-green-500/20 text-[11px] h-7 px-2" data-testid={`unblock-${device.device_id}`}>
                            <CheckCircle className="h-3 w-3 mr-1" /> Unblock
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={() => toggleBlock(device.device_id, true)}
                            className="text-red-400 hover:bg-red-500/20 text-[11px] h-7 px-2" data-testid={`block-${device.device_id}`}>
                            <Ban className="h-3 w-3 mr-1" /> Block
                          </Button>
                        )}
                        {device.is_trusted && !device.is_blocked && (
                          <Button size="sm" variant="ghost" onClick={() => untrustDevice(device.device_id)}
                            className="text-amber-400 hover:bg-amber-500/20 text-[11px] h-7 px-2" data-testid={`untrust-${device.device_id}`}>
                            <ShieldOff className="h-3 w-3 mr-1" /> Untrust
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogClose asChild>
            <Button variant="outline" className="w-full border-slate-700 text-slate-300 hover:bg-slate-800">
              Close
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SecurityDialog;
